import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useLiveQuery } from 'dexie-react-hooks'
import type { BadgeGroupItem } from '@mytours/components'
import { FileRec } from 'mytours-read-api-typescript'
import { toggleBadgeModal } from 'mytours-read-common/src/badges/actions'
import db from 'mobile_web/modules/db'
import { DexieBadge } from 'mobile_web/modules/db/types'
import { ImageSizePreset } from '../../common_lib/types'
import { generateImageSrcSet, generateImageUrl } from '../../common_lib/utils'

// Missing artwork is left to the tile, which draws its own placeholder.
export const badgeArtwork = (file?: FileRec): Pick<BadgeGroupItem, 'image' | 'imageSrcSet'> => {
	if (!file) {
		return {}
	}
	const focalPoint = file.focal_point
	const options = {
		imageSizePreset: ImageSizePreset.Grid,
		crop: Boolean(focalPoint && (focalPoint[0] !== 0.5 || focalPoint[1] !== 0.5)),
		focalPoint,
	}
	return { image: generateImageUrl(file, options), imageSrcSet: generateImageSrcSet(file, options) }
}

// `undefined` until the query resolves, so a caller can tell "nothing earned" from "not known yet".
export const useEarnedBadgeIds = (): Set<number> | undefined => {
	const earned = useLiveQuery(() => db.recentlyEarnedBadges.toArray())
	return useMemo(() => earned && new Set(earned.map(item => item.badge_id)), [earned])
}

// The badges flagged for the list, plus any earned one whatever its flag says.
export const listedBadges = (badges: DexieBadge[], earnedIds: Set<number>): DexieBadge[] =>
	badges.filter(badge => badge.visible_on_badge_list || earnedIds.has(badge.id)).sort((a, b) => a.position - b.position)

// An empty group is 0, not NaN.
export const groupProgress = (badges: DexieBadge[], earnedIds: Set<number>): number =>
	badges.length === 0 ? 0 : Math.round((badges.filter(badge => earnedIds.has(badge.id)).length / badges.length) * 100)

export const useOpenBadge = (): ((badge: DexieBadge, earned: boolean) => void) => {
	const dispatch = useDispatch()
	return useCallback(
		(badge: DexieBadge, earned: boolean) =>
			dispatch(toggleBadgeModal({ show: true, badgeId: badge.id, shouldBePushedToEarned: false, earned })),
		[dispatch],
	)
}

// The plain title is the fallback, so a missing wording does not leave the control unnamed.
export const toBadgeItem = (badge: DexieBadge, earned: boolean, onClick: () => void): BadgeGroupItem => ({
	id: `badge-${badge.id}`,
	title: (earned ? badge.earned_title : badge.unearned_title) || badge.title || '',
	earned,
	onClick,
	...badgeArtwork(badge.badge_media_item?.file),
})
